import React, { FC, useCallback, useEffect, useState } from 'react';
import { useTranslate } from '@/translations';
import { useCreateInspectionsVehiclesFlatRateCode } from '@/api/Inspections/hooks/useInspectionsVehicles';

import { Alert, App, Form, Input, InputNumber, Modal, Spin } from 'antd';
import { CreateFlateRateData, FlatRate } from '@/types';
import { confirmDiscardUnsaved } from '../confirmDiscardUnsaved';
import {
  isSaveNotesShortcut,
  saveNotesShortcutHint,
} from '../preTimingShortcuts';

interface CreateFlatRateCodeProps {
  inspectionId: number;
  isPopupVisible: boolean;
  togglePopup: (value: boolean) => void;
  onSubmit?: (data: FlatRate) => void;
}

export const CreateFlatRateCodePopup: FC<CreateFlatRateCodeProps> = ({
  inspectionId,
  isPopupVisible,
  togglePopup,
  onSubmit,
}) => {
  const { t } = useTranslate();
  const { modal } = App.useApp();
  const [form] = Form.useForm<CreateFlateRateData>();

  const { mutateAsync: mutateAsyncCreateFRCode, isPending } =
    useCreateInspectionsVehiclesFlatRateCode();
  const [error, setError] = useState<string>();

  const resetForm = useCallback(() => {
    form.resetFields();
    form.setFieldsValue({ frCode: '', description: '', cost: 0 });
  }, [form]);

  const handleCloseForm = () => {
    togglePopup(false);
    resetForm();
  };

  const requestClose = () => {
    if (isPending) return;
    if (!form.isFieldsTouched()) {
      handleCloseForm();
      return;
    }
    confirmDiscardUnsaved(
      modal,
      {
        title: t('discardUnsavedChanges'),
        content: t('discardUnsavedChangesBody'),
        okText: t('yes'),
        cancelText: t('no'),
      },
      handleCloseForm,
    );
  };

  useEffect(() => {
    resetForm();
  }, [form, resetForm]);

  const handleSubmit = async (data: CreateFlateRateData) => {
    if (data.frCode.length === 0) {
      setError('Please input the code');
      return;
    }
    if (data.cost <= 0) {
      setError('The cost must be greater than 0');
      return;
    }

    const body = {
      inspectionId,
      flatRate: data.frCode,
      description: data.description,
      price: data.cost,
    };

    const res = await mutateAsyncCreateFRCode(body);
    if (res.success) {
      onSubmit?.({
        ...body,
        id: res.data,
      });
      handleCloseForm();
    } else {
      setError(res.message?.Message || 'Something went wrong...');
    }
  };

  return (
    <Modal
      open={isPopupVisible}
      onCancel={requestClose}
      title={t('createFrCode')}
      centered
      okText={t('create')}
      onOk={() => form.submit()}
      confirmLoading={isPending}
      keyboard={!isPending}
      footer={(_, { OkBtn }) => <OkBtn />}
    >
      <Spin spinning={isPending}>
        <Form
          id="create-flat-rate-form"
          form={form}
          onFinish={handleSubmit}
          labelCol={{ flex: '0 0 100px' }}
          onChange={() => setError(undefined)}
        >
          <Form.Item label={t('frCode')} name="frCode">
            <Input maxLength={7} onPressEnter={() => form.submit()} />
          </Form.Item>

          <Form.Item label={t('cost')} name="cost">
            <InputNumber
              min={0}
              className="w-100"
              prefix="£"
              onPressEnter={() => form.submit()}
            />
          </Form.Item>

          <Form.Item
            label={t('description')}
            name="description"
            extra={t('shortcutSaveNotes', {
              shortcut: saveNotesShortcutHint(),
            })}
          >
            <Input.TextArea
              rows={5}
              maxLength={45}
              onKeyDown={(event) => {
                if (!isSaveNotesShortcut(event.nativeEvent)) return;
                event.preventDefault();
                form.submit();
              }}
            />
          </Form.Item>

          {error ? (
            <Alert message={error} type="error" className="mb-4" />
          ) : null}
        </Form>
      </Spin>
    </Modal>
  );
};
